import { useState, useEffect } from 'react';
import { X, Plus, MapPin, Loader2, FileText, Navigation, Phone, Tag, Image, Calendar, Eye, Sparkles, Users, Store } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { AdminProducer, AVAILABLE_CATEGORIES, REGIONS } from '@/data/admin/mockProducers';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { RichTextEditor } from '@/components/common/RichTextEditor';

export interface ProducerFormData {
  name: string;
  shortDescription: string;
  longDescription: string;
  address: string;
  city: string;
  region: string;
  lat: string;
  lng: string;
  categories: string[];
  phone: string;
  website: string;
  instagram: string;
  email: string;
  openingHoursText: string;
  coverUrl: string;
  galleryUrls: string[];
  status: 'draft' | 'public';
  verified: boolean;
}

const emptyFormData: ProducerFormData = {
  name: '',
  shortDescription: '',
  longDescription: '',
  address: '',
  city: '',
  region: '',
  lat: '',
  lng: '',
  categories: [],
  phone: '',
  website: '',
  instagram: '',
  email: '',
  openingHoursText: '',
  coverUrl: '',
  galleryUrls: [''],
  status: 'draft',
  verified: false,
};

interface ProducerFormProps {
  initialData?: AdminProducer;
  onSubmit: (data: ProducerFormData) => void;
  onCancel: () => void;
  isEditing?: boolean;
  isSubmitting?: boolean;
  featuresSection?: React.ReactNode;
  eventsSection?: React.ReactNode;
  farmShopSection?: React.ReactNode;
  teamSection?: React.ReactNode;
  dangerZone?: React.ReactNode;
}

interface NavItem {
  value: string;
  label: string;
  icon: React.ElementType;
  show: boolean;
}

export function ProducerForm({
  initialData,
  onSubmit,
  onCancel,
  isEditing = false,
  isSubmitting = false,
  featuresSection,
  eventsSection,
  farmShopSection,
  teamSection,
  dangerZone,
}: ProducerFormProps) {
  const [formData, setFormData] = useState<ProducerFormData>(emptyFormData);
  const [customCategory, setCustomCategory] = useState('');
  const [showLongDescription, setShowLongDescription] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ProducerFormData, string>>>({});
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeResult, setGeocodeResult] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        shortDescription: initialData.shortDescription,
        longDescription: initialData.longDescription || '',
        address: initialData.address,
        city: initialData.city,
        region: initialData.region,
        lat: initialData.lat.toString(),
        lng: initialData.lng.toString(),
        categories: initialData.categories,
        phone: initialData.phone || '',
        website: initialData.website || '',
        instagram: initialData.instagram || '',
        email: initialData.email || '',
        openingHoursText: initialData.openingHoursText || '',
        coverUrl: initialData.coverUrl || '',
        galleryUrls: initialData.galleryUrls.length > 0 ? initialData.galleryUrls : [''],
        status: initialData.status,
        verified: initialData.verified,
      });
      if (initialData.longDescription) {
        setShowLongDescription(true);
      }
    }
  }, [initialData]);

  const updateField = <K extends keyof ProducerFormData>(
    key: K,
    value: ProducerFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const toggleCategory = (category: string) => {
    const current = formData.categories;
    if (current.includes(category)) {
      updateField('categories', current.filter((c) => c !== category));
    } else {
      updateField('categories', [...current, category]);
    }
  };

  const addCustomCategory = () => {
    const trimmed = customCategory.trim();
    if (trimmed && !formData.categories.includes(trimmed)) {
      updateField('categories', [...formData.categories, trimmed]);
      setCustomCategory('');
    }
  };

  const updateGalleryUrl = (index: number, value: string) => {
    const updated = [...formData.galleryUrls];
    updated[index] = value;
    updateField('galleryUrls', updated);
  };

  const addGalleryUrl = () => {
    if (formData.galleryUrls.length < 3) {
      updateField('galleryUrls', [...formData.galleryUrls, '']);
    }
  };

  const removeGalleryUrl = (index: number) => {
    const updated = formData.galleryUrls.filter((_, i) => i !== index);
    updateField('galleryUrls', updated.length > 0 ? updated : ['']);
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ProducerFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Namn är obligatoriskt';
    }
    // short_description is now optional - only validate length if provided
    if (formData.shortDescription.length > 500) {
      newErrors.shortDescription = 'Max 500 tecken';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleGeocode = async () => {
    if (!formData.address || formData.address.trim().length < 4) {
      toast({
        title: 'Adress saknas',
        description: 'Fyll i en adress först.',
        variant: 'destructive',
      });
      return;
    }

    setIsGeocoding(true);
    setGeocodeResult(null);

    try {
      const response = await supabase.functions.invoke('geocode-address', {
        body: {
          address: formData.address,
          city: formData.city || undefined,
          region: formData.region || undefined,
          country: 'Sweden',
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Geocoding failed');
      }

      const data = response.data;
      
      if (data.error === 'not_found') {
        toast({
          title: 'Kunde inte hitta adressen',
          description: 'Prova att lägga till ort eller kontrollera stavningen.',
          variant: 'destructive',
        });
        return;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      const lat = typeof data.lat === 'number' && Number.isFinite(data.lat) ? data.lat : null;
      const lng = typeof data.lng === 'number' && Number.isFinite(data.lng) ? data.lng : null;

      if (lat === null || lng === null) {
        toast({
          title: 'Ogiltiga koordinater',
          description: 'Svaret innehöll inga giltiga koordinater. Kontrollera adressen.',
        });
        return;
      }

      updateField('lat', lat.toString());
      updateField('lng', lng.toString());
      setGeocodeResult(data.display_name);

      toast({
        title: 'Koordinater uppdaterade',
        description: `Lat: ${lat.toFixed(5)}, Lng: ${lng.toFixed(5)}`,
      });
    } catch (error) {
      console.error('Geocoding error:', error);
      toast({
        title: 'Något gick fel',
        description: 'Kunde inte hämta koordinater. Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setIsGeocoding(false);
    }
  };

  const shortDescriptionCount = formData.shortDescription.length;

  // Navigation items with conditional visibility
  const navItems: NavItem[] = [
    { value: 'grundinfo', label: 'Grundinfo', icon: FileText, show: true },
    { value: 'plats', label: 'Plats', icon: Navigation, show: true },
    { value: 'kontakt', label: 'Kontakt', icon: Phone, show: true },
    { value: 'kategorier', label: 'Kategorier', icon: Tag, show: true },
    { value: 'bilder', label: 'Bilder', icon: Image, show: true },
    { value: 'events', label: 'Kurser & event', icon: Calendar, show: !!eventsSection },
    { value: 'farmshop', label: 'Gårdsbutik online', icon: Store, show: !!farmShopSection },
    { value: 'publicering', label: 'Publicering', icon: Eye, show: true },
    { value: 'features', label: 'Features', icon: Sparkles, show: !!featuresSection },
    { value: 'team', label: 'Team', icon: Users, show: !!(teamSection || dangerZone) },
  ];

  const visibleNavItems = navItems.filter(item => item.show);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
      <Tabs defaultValue="grundinfo" className="flex-1" orientation="vertical">
        {/* Grid layout: sidebar + content */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 lg:gap-8">
          {/* Sidebar navigation */}
          <div className="lg:sticky lg:top-20 lg:self-start">
            {/* Mobile: horizontal scroll */}
            <TabsList className="flex lg:hidden w-full justify-start overflow-x-auto flex-nowrap bg-muted/50 p-1.5 h-auto gap-1 rounded-lg">
              {visibleNavItems.map((item) => (
                <TabsTrigger
                  key={item.value}
                  value={item.value}
                  className="whitespace-nowrap text-sm px-3 py-1.5"
                >
                  {item.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Desktop: vertical menu */}
            <TabsList className="hidden lg:flex flex-col w-full h-auto bg-transparent p-0 gap-1">
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger
                    key={item.value}
                    value={item.value}
                    className={cn(
                      "w-full justify-start gap-3 px-3 py-2.5 text-sm font-medium rounded-lg",
                      "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                      "data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {item.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {/* Content area */}
          <div className="min-w-0">
            {/* Section: Grundinfo */}
            <TabsContent value="grundinfo" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
              <div>
                <h3 className="text-lg font-medium text-foreground">Namn och beskrivning</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Grundläggande information om producenten.
                </p>
              </div>

              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label htmlFor="name">Namn *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="T.ex. Björkeby Gård"
                    className={cn(errors.name && 'border-destructive')}
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="shortDescription">Kort beskrivning (valfritt)</Label>
                    <span
                      className={cn(
                        'text-xs',
                        shortDescriptionCount > 500 ? 'text-destructive' : 'text-muted-foreground'
                      )}
                    >
                      {shortDescriptionCount}/500
                    </span>
                  </div>
                  <Textarea
                    id="shortDescription"
                    value={formData.shortDescription}
                    onChange={(e) => updateField('shortDescription', e.target.value)}
                    placeholder="En kort teaser om vad gården erbjuder."
                    rows={2}
                    className={cn(errors.shortDescription && 'border-destructive')}
                  />
                  {errors.shortDescription && (
                    <p className="text-xs text-destructive mt-1">{errors.shortDescription}</p>
                  )}
                </div>

                {!showLongDescription ? (
                  <button
                    type="button"
                    onClick={() => setShowLongDescription(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    + Lägg till längre beskrivning
                  </button>
                ) : (
                  <div>
                    <Label htmlFor="longDescription">Längre beskrivning</Label>
                    {initialData?.id ? (
                      <RichTextEditor
                        value={formData.longDescription}
                        onChange={(html) => updateField('longDescription', html)}
                        producerId={initialData.id}
                        placeholder="Berätta mer om gårdens historia, produktion eller filosofi. Du kan lägga till länkar och bilder."
                        expandable
                        expandedTitle="Längre beskrivning"
                        expandedSurface="admin_producer"
                        expandedField="long_description"
                      />
                    ) : (
                      <Textarea
                        id="longDescription"
                        value={formData.longDescription}
                        onChange={(e) => updateField('longDescription', e.target.value)}
                        placeholder="Berätta mer om gårdens historia, produktion eller filosofi."
                        rows={5}
                      />
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {initialData?.id ? 'Du kan formatera texten och lägga till länkar och bilder.' : 'Spara först för att kunna lägga till bilder och länkar.'}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Section: Plats */}
            <TabsContent value="plats" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
              <div>
                <h3 className="text-lg font-medium text-foreground">Adress och position</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Var finns producenten?
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                <div className="md:col-span-2">
                  <Label htmlFor="address">Adress</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="T.ex. Gårdsvägen 12"
                  />
                </div>

                <div>
                  <Label htmlFor="city">Ort</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="T.ex. Gnesta"
                  />
                </div>

                <div>
                  <Label htmlFor="region">Landskap/Län</Label>
                  <Select
                    value={formData.region}
                    onValueChange={(value) => updateField('region', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Välj region" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover">
                      {REGIONS.map((region) => (
                        <SelectItem key={region} value={region}>
                          {region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="lat">Latitud</Label>
                  <Input
                    id="lat"
                    type="number"
                    step="any"
                    value={formData.lat}
                    onChange={(e) => updateField('lat', e.target.value)}
                    placeholder="59.0512"
                  />
                </div>

                <div>
                  <Label htmlFor="lng">Longitud</Label>
                  <Input
                    id="lng"
                    type="number"
                    step="any"
                    value={formData.lng}
                    onChange={(e) => updateField('lng', e.target.value)}
                    placeholder="17.3078"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={handleGeocode}
                    disabled={isGeocoding || !formData.address || formData.address.trim().length < 4}
                  >
                    {isGeocoding ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Hämtar…
                      </>
                    ) : (
                      <>
                        <MapPin className="h-4 w-4 mr-2" />
                        {formData.lat && formData.lng ? 'Uppdatera från adress' : 'Hämta från adress'}
                      </>
                    )}
                  </Button>
                  {geocodeResult && (
                    <p className="text-xs text-muted-foreground">
                      Hittade: {geocodeResult}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {!formData.address || formData.address.trim().length < 4 
                      ? 'Fyll i adress först för att hämta koordinater.'
                      : 'Tips: Skriv gärna "Gårdsvägen 12, Gnesta" för bättre träff.'}
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Section: Kontakt */}
            <TabsContent value="kontakt" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
              <div>
                <h3 className="text-lg font-medium text-foreground">Kontaktuppgifter</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Hur kan besökare nå producenten?
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                <div>
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="070-123 45 67"
                  />
                </div>

                <div>
                  <Label htmlFor="email">E-post</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="hej@gard.se"
                  />
                </div>

                <div>
                  <Label htmlFor="website">Webbplats</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="https://gard.se"
                  />
                </div>

                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    value={formData.instagram}
                    onChange={(e) => updateField('instagram', e.target.value)}
                    placeholder="@gard"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="openingHoursText">Öppettider</Label>
                  <Textarea
                    id="openingHoursText"
                    value={formData.openingHoursText}
                    onChange={(e) => updateField('openingHoursText', e.target.value)}
                    placeholder="T.ex. Fre–Sön 11–17. Sommartid dagligen."
                    rows={2}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Section: Kategorier */}
            <TabsContent value="kategorier" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
              <div>
                <h3 className="text-lg font-medium text-foreground">Kategorier</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Vad erbjuder producenten?
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {AVAILABLE_CATEGORIES.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-sm font-medium transition-colors border',
                      formData.categories.includes(category)
                        ? 'bg-primary/15 border-primary/30 text-primary'
                        : 'bg-card border-border text-muted-foreground hover:border-primary/30'
                    )}
                  >
                    {category}
                  </button>
                ))}
                {formData.categories
                  .filter((c) => !AVAILABLE_CATEGORIES.includes(c))
                  .map((custom) => (
                    <button
                      key={custom}
                      type="button"
                      onClick={() => toggleCategory(custom)}
                      className="px-3 py-1.5 rounded-full text-sm font-medium bg-accent/15 border border-accent/30 text-accent flex items-center gap-1"
                    >
                      {custom}
                      <X className="h-3 w-3" />
                    </button>
                  ))}
              </div>

              <div className="flex items-center gap-2">
                <Input
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="Lägg till kategori…"
                  className="max-w-xs"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomCategory();
                    }
                  }}
                />
                <Button type="button" variant="outline" size="icon" onClick={addCustomCategory}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </TabsContent>

            {/* Section: Bilder */}
            <TabsContent value="bilder" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
              <div>
                <h3 className="text-lg font-medium text-foreground">Bilder</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Lägg till bildlänkar (uppladdning kommer snart).
                </p>
              </div>

              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label htmlFor="coverUrl">Omslagsbild URL</Label>
                  <Input
                    id="coverUrl"
                    value={formData.coverUrl}
                    onChange={(e) => updateField('coverUrl', e.target.value)}
                    placeholder="https://example.com/bild.jpg"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Bildgalleri URL:er (max 3)</Label>
                  {formData.galleryUrls.map((url, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={url}
                        onChange={(e) => updateGalleryUrl(index, e.target.value)}
                        placeholder={`Bild ${index + 1} URL`}
                      />
                      {formData.galleryUrls.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeGalleryUrl(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {formData.galleryUrls.length < 3 && (
                    <Button type="button" variant="outline" size="sm" onClick={addGalleryUrl}>
                      <Plus className="h-4 w-4 mr-2" />
                      Lägg till bild
                    </Button>
                  )}
                </div>

                <Button type="button" variant="outline" size="sm" disabled>
                  Ladda upp
                  <Badge variant="secondary" className="ml-2 text-[10px]">
                    Kommer snart
                  </Badge>
                </Button>
              </div>
            </TabsContent>

            {/* Section: Kurser & aktiviteter (only shown if feature enabled) */}
            {eventsSection && (
              <TabsContent value="events" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
                <div>
                  <h3 className="text-lg font-medium text-foreground">Kurser & aktiviteter</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Skapa och hantera kurser, workshops och aktiviteter.
                  </p>
                </div>
                {eventsSection}
              </TabsContent>
            )}

            {/* Section: Gårdsbutik online (only shown if feature enabled) */}
            {farmShopSection && (
              <TabsContent value="farmshop" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
                <div>
                  <h3 className="text-lg font-medium text-foreground">Gårdsbutik online</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Hantera produkter som säljs direkt från gården.
                  </p>
                </div>
                {farmShopSection}
              </TabsContent>
            )}

            {/* Section: Publicering */}
            <TabsContent value="publicering" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
              <div>
                <h3 className="text-lg font-medium text-foreground">Synlighet</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Kontrollera synlighet och verifiering.
                </p>
              </div>

              <div className="space-y-4 max-w-2xl">
                <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
                  <div>
                    <p className="font-medium text-foreground">Status</p>
                    <p className="text-sm text-muted-foreground">
                      {formData.status === 'public'
                        ? 'Syns i Explore och på detaljsidan'
                        : 'Syns inte publikt'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'text-sm font-medium',
                        formData.status === 'draft' ? 'text-amber-600' : 'text-muted-foreground'
                      )}
                    >
                      Draft
                    </span>
                    <Switch
                      checked={formData.status === 'public'}
                      onCheckedChange={(checked) =>
                        updateField('status', checked ? 'public' : 'draft')
                      }
                    />
                    <span
                      className={cn(
                        'text-sm font-medium',
                        formData.status === 'public' ? 'text-primary' : 'text-muted-foreground'
                      )}
                    >
                      Public
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
                  <div>
                    <p className="font-medium text-foreground">Verifierad</p>
                    <p className="text-sm text-muted-foreground">
                      Markerar att uppgifterna är bekräftade
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-muted-foreground">
                      Nej
                    </span>
                    <Switch
                      checked={formData.verified}
                      onCheckedChange={(checked) => updateField('verified', checked)}
                    />
                    <span
                      className={cn(
                        'text-sm font-medium',
                        formData.verified ? 'text-primary' : 'text-muted-foreground'
                      )}
                    >
                      Ja
                    </span>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Section: Features (only shown if featuresSection provided) */}
            {featuresSection && (
              <TabsContent value="features" className="mt-0 space-y-6 data-[state=inactive]:hidden" forceMount>
                <div>
                  <h3 className="text-lg font-medium text-foreground">Features</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Aktivera eller inaktivera extrafunktioner för denna producent.
                  </p>
                </div>
                <div className="max-w-2xl">
                  {featuresSection}
                </div>
              </TabsContent>
            )}

            {/* Section: Team (only shown if teamSection or dangerZone provided) */}
            {(teamSection || dangerZone) && (
              <TabsContent value="team" className="mt-0 space-y-8 data-[state=inactive]:hidden" forceMount>
                {teamSection && (
                  <div>
                    {teamSection}
                  </div>
                )}

                {dangerZone && (
                  <div className="pt-8 border-t border-border max-w-2xl">
                    {dangerZone}
                  </div>
                )}
              </TabsContent>
            )}

            {/* Sticky action bar */}
            <div className="sticky bottom-0 left-0 right-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-t border-border mt-8 py-4 -mx-4 px-4 lg:mx-0 lg:px-0">
              <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                  Avbryt
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sparar...' : isEditing ? 'Spara ändringar' : 'Spara'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Tabs>
    </form>
  );
}
